import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

const ProtectedRoute = ({ children, requiredRole = null, allowedRoles = null, requireAuth = true }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while authentication is being checked
  if (isLoading) {
    return (
      <div className="loading-container">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // If authentication is required but user is not logged in
  if (requireAuth && !user) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // If allowedRoles prop is provided, ensure user's role is permitted
  if (allowedRoles && (!user || !allowedRoles.includes(user.role))) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Staff: only allow paths that are in their assigned modules (or the Staff page)
  if (user?.role === 'staff') {
    const path = location.pathname || '';
    if (path === '/dashboard') return children;
    if (path === '/admin/staff') return children;
    if (Array.isArray(user.allowedModules) && user.allowedModules.length > 0) {
      const allowed = user.allowedModules.some((p) => path === p || path.startsWith(p + '/'));
      if (!allowed) return <Navigate to="/dashboard" replace />;
    } else if (path.startsWith('/admin') || path.startsWith('/')) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // If specific role is required but user doesn't have it
  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to="/unauthorized" replace />;
  }

  // If user is logged in but trying to access auth pages (login, register)
  if (!requireAuth && user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default ProtectedRoute;